import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRM_PHRASE = 'DELETE EVENTS';

/**
 * Admin-only bulk event delete modal.
 *
 * Two-step flow:
 *   1. Fetch preview counts from backend (no writes).
 *   2. Require the admin to type the literal phrase "DELETE EVENTS" before the
 *      destructive button becomes clickable.
 *
 * The modal displays:
 *   - All selected event titles
 *   - Counts that WILL be deleted (events, tickets, discount codes, event
 *     messages, activity log rows)
 *   - An explicit "not deleted" note for comp guests / media / users / hosts
 */
export default function BulkDeleteEventsModal({ selectedEvents, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const ids = selectedEvents.map(e => e.event_id);
        const res = await axios.post(
          `${API}/admin/events/bulk-delete/preview`,
          { event_ids: ids },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPreview(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Preview failed');
        onClose();
      } finally {
        setLoadingPreview(false);
      }
    };
    if (selectedEvents && selectedEvents.length > 0) fetchPreview();
  }, [selectedEvents, onClose]);

  const canDelete = confirmText === CONFIRM_PHRASE && !loadingPreview && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('session_token');
      const ids = selectedEvents.map(e => e.event_id);
      const res = await axios.post(
        `${API}/admin/events/bulk-delete`,
        { event_ids: ids, confirmation: CONFIRM_PHRASE },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = res.data.deleted || {};
      toast.success(
        `Deleted ${d.events} event(s) · ${d.tickets} ticket(s) · ${d.event_messages} message(s) · ${d.activity_log} activity row(s)`
      );
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setDeleting(false);
    }
  };

  if (!selectedEvents || selectedEvents.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="bulk-delete-modal"
      onKeyDown={(e) => {
        // Don't let Enter accidentally submit unless confirmation is valid.
        if (e.key === 'Enter' && !canDelete) e.preventDefault();
      }}
    >
      <div className="w-full max-w-2xl bg-[#0A0A0A] border border-[#C44545]/40 rounded-sm shadow-2xl my-8 sm:my-0">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#1A1A1A] flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#C44545] flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-[#EAE7E1]">Bulk Delete Events</h2>
              <p className="text-xs text-[#777]">This action cannot be undone.</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#777] hover:text-[#EAE7E1]" data-testid="bulk-delete-close-btn" disabled={deleting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Selected titles */}
          <div>
            <div className="text-[11px] tracking-wider text-[#777] uppercase font-semibold mb-2">
              Selected ({selectedEvents.length})
            </div>
            <ul className="space-y-1 max-h-40 overflow-y-auto bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid="bulk-delete-titles">
              {selectedEvents.map(e => (
                <li key={e.event_id} className="text-sm text-[#EAE7E1] truncate">
                  <span className="text-[#555] font-mono text-xs mr-2">{e.event_id}</span>
                  {e.title}
                </li>
              ))}
            </ul>
          </div>

          {/* Counts */}
          <div>
            <div className="text-[11px] tracking-wider text-[#777] uppercase font-semibold mb-2">
              Will be deleted
            </div>
            {loadingPreview ? (
              <div className="text-sm text-[#AFAFAF]">Counting…</div>
            ) : preview ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="bulk-delete-counts">
                {[
                  ['Events', preview.event_count, 'events'],
                  ['Tickets', preview.ticket_count, 'tickets'],
                  ['Discount codes', preview.discount_code_count, 'discount-codes'],
                  ['Event messages', preview.event_message_count, 'event-messages'],
                  ['Activity rows', preview.activity_log_count, 'activity-log'],
                ].map(([label, val, key]) => (
                  <div key={key} className="bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid={`bulk-count-${key}`}>
                    <div className="text-[10px] text-[#777] uppercase tracking-wide">{label}</div>
                    <div className="text-xl font-bold text-[#C44545] mt-1">{val}</div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Not deleted explainer */}
          <div className="bg-[#050505] border border-[#1A1A1A] rounded-sm p-3" data-testid="bulk-delete-notes">
            <div className="text-[11px] tracking-wider text-[#777] uppercase font-semibold mb-1">
              Not deleted
            </div>
            <p className="text-xs text-[#AFAFAF] leading-relaxed">
              This permanently deletes selected events and their event-scoped tickets/messages/discounts/activity.
              <span className="text-[#EAE7E1]"> Users, host accounts, host defaults, saved payment methods, saved pass tier templates, comp-guest lists, gift codes, platform settings, and unrelated media will not be deleted.</span>
            </p>
          </div>

          {/* Confirmation phrase */}
          <div>
            <label htmlFor="bulk-delete-confirm" className="block text-[11px] tracking-wider text-[#777] uppercase font-semibold mb-2">
              Type <span className="text-[#C44545] font-mono">{CONFIRM_PHRASE}</span> to enable delete
            </label>
            <Input
              id="bulk-delete-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              disabled={deleting}
              data-testid="bulk-delete-confirm-input"
              className="font-mono"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#1A1A1A] flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            onClick={onClose}
            disabled={deleting}
            variant="outline"
            className="border-[#1A1A1A] text-[#AFAFAF] rounded-sm"
            data-testid="bulk-delete-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={!canDelete}
            className="bg-[#C44545] hover:bg-[#E25555] text-white rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="bulk-delete-confirm-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {deleting ? 'Deleting…' : `Delete ${selectedEvents.length} event${selectedEvents.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
